requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    const SECTION_TAG = 'product-lists-section';
    const HIDE_DUPLICATE_STORAGE_KEY = 'compare:hideDuplicateValues:enabled';
    const HIDE_DUPLICATE_SYNC_EVENT = 'compare-hide-duplicate-values:sync';

    const getMode = (el) =>
      el.getAttribute?.('mode') ||
      el.closest?.(SECTION_TAG)?.getAttribute('mode') ||
      el.closest?.(SECTION_TAG)?.dataset.mode ||
      'compare';

    const copy = async (text) => {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {}
      document.body.removeChild(ta);
      return true;
    };

    class ProductListsBaseButton extends HTMLElement {
      connectedCallback() {
        if (this._booted) return;
        this._booted = true;
        this.btn = this.querySelector('button');
        this.onConnect?.();
        this.addEventListener('click', (e) => {
          if (e.target.closest('button') !== this.btn) return;
          this.onClick?.(e);
        });
      }
    }

    class ProductListsShareButton extends ProductListsBaseButton {
      async onClick() {
        const mode = getMode(this);
        const url = window.ProductLists?.getShareURL?.(mode) || location.href;
        if (navigator.share) {
          try {
            await navigator.share({
              title: document.title,
              url
            });
            this.classList.add('is-copied');
            setTimeout(() => this.classList.remove('is-copied'), 900);
            return;
          } catch {}
        }
        await copy(url);
        this.classList.add('is-copied');
        setTimeout(() => this.classList.remove('is-copied'), 900);
      }
    }

    class ProductListsPrintButton extends ProductListsBaseButton {
      onClick() {
        window.print();
      }
    }

    class ProductListsImportSharedButton extends ProductListsBaseButton {
      async onClick() {
        this.classList.add('disabled');
        const mode = getMode(this);
        await window.ProductLists.importFromURL(mode, {
          append: true
        });
        window.ProductLists.constructor.stripAllQueryFromLocation();
        this.classList.remove('disabled');
      }
    }

    class ProductListsRemoveButton extends ProductListsBaseButton {
      async onClick() {
        this.classList.add('disabled');
        const mode = getMode(this);
        const vid = Number(this.getAttribute('vid'));
        const handle = String(this.getAttribute('handle') || '');
        if (!Number.isFinite(vid) || !handle) return;
        await window.ProductLists.remove(mode, vid, handle);
        this.classList.remove('disabled');
      }
    }

    class CompareClearGroupButton extends ProductListsBaseButton {
      async onClick() {
        const button = this.closest('.button');
        button.classList.add('disabled');
        const mode = getMode(this);
        if (mode !== 'compare') return;
        const handlesCSV = String(this.getAttribute('handles') || '').trim();
        if (!handlesCSV) return;
        const handles = new Set(handlesCSV.split(',').map((s) => s.trim()).filter(Boolean));
        await window.ProductLists.removeMany('compare', ({
          handle
        }) => handles.has(handle));
        button.remove('disabled');
      }
    }

    class ProductListsClearAllButton extends ProductListsBaseButton {
      async onClick() {
        this.classList.add('disabled');
        const mode = getMode(this);
        await window.ProductLists.clear(mode);
        this.classList.remove('disabled');
      }
    }

    class CompareHideDuplicateValues extends HTMLElement {
      connectedCallback() {
        if (this._booted) return;
        this._booted = true;
        this.cb = this.querySelector('input[type="checkbox"]');
        this.section = this.closest(SECTION_TAG) || null;
        this.syncRoot = this.section || document;
        if (!this.cb) return;
        const stored = this.getStoredState();
        if (stored !== null) {
          this.cb.checked = stored;
        }
        this.apply();
        this.broadcastState();
        this.cb.addEventListener('change', () => {
          this.storeState(this.cb.checked);
          this.apply();
          this.broadcastState();
        });
      }
      getStoredState() {
        try {
          const saved = localStorage.getItem(HIDE_DUPLICATE_STORAGE_KEY);
          if (saved === '1') return true;
          if (saved === '0') return false;
        } catch {}
        return null;
      }
      storeState(checked) {
        try {
          localStorage.setItem(HIDE_DUPLICATE_STORAGE_KEY, checked ? '1' : '0');
        } catch {}
      }
      broadcastState(origin = 'master') {
        if (!this.syncRoot) return;
        try {
          this.syncRoot.dispatchEvent(
            new CustomEvent(HIDE_DUPLICATE_SYNC_EVENT, {
              detail: {
                checked: !!this.cb?.checked,
                origin
              }
            })
          );
        } catch {}
      }
      apply() {
        const sec = this.section || this.closest(SECTION_TAG);
        if (!sec || !this.cb) return;
        this.section = sec;
        sec.classList.toggle('is-compare-hide-duplicate-values', !!this.cb.checked);
      }
    }

    class CompareHideDuplicateValuesClone extends HTMLElement {
      connectedCallback() {
        if (this._booted) return;
        this._booted = true;
        this.cb = this.querySelector('input[type="checkbox"]');
        this.syncRoot = this.closest(SECTION_TAG) || document;
        if (!this.cb) return;
        this._boundSync = this.handleMasterSync.bind(this);
        this.syncRoot.addEventListener(HIDE_DUPLICATE_SYNC_EVENT, this._boundSync);
        this.cb.addEventListener('change', () => this.propagateChange());
        this.syncFromMaster();
      }
      disconnectedCallback() {
        if (this.syncRoot && this._boundSync) {
          this.syncRoot.removeEventListener(HIDE_DUPLICATE_SYNC_EVENT, this._boundSync);
        }
      }
      resolveMasterInput() {
        const root = this.syncRoot || document;
        const master = root.querySelector?.('compare-hide-duplicate-values-toggle');
        return master?.querySelector?.('input[type="checkbox"]') || null;
      }
      handleMasterSync(event) {
        if (event?.target !== this.syncRoot) return;
        const checked = !!event.detail?.checked;
        if (!this.cb) return;
        if (this.cb.checked !== checked) {
          this.cb.checked = checked;
        }
      }
      syncFromMaster() {
        const input = this.resolveMasterInput();
        if (!input || !this.cb) return;
        this.cb.checked = input.checked;
      }
      propagateChange() {
        const input = this.resolveMasterInput();
        if (!input || !this.cb) return;
        if (input.checked === this.cb.checked) return;
        input.checked = this.cb.checked;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      }
    }

    const TabsManager = (() => {
      const STORAGE_PREFIX = 'compare:tabs:selected:';
      const INIT_ATTR = 'data-tabs-bound';

      function storageKeyFor(tabs) {
        const grouping = tabs.getAttribute('data-grouping') || 'group';
        return STORAGE_PREFIX + grouping;
      }

      function restore(tabs) {
        try {
          const key = storageKeyFor(tabs);
          const saved = localStorage.getItem(key);
          if (!saved) return;
          const radio = tabs.querySelector('.compare-tabs__input[data-group="' + CSS.escape(saved) + '"]');
          if (radio) radio.checked = true;
        } catch {}
      }

      function bind(tabs) {
        if (tabs.getAttribute(INIT_ATTR) === '1') return;
        tabs.setAttribute(INIT_ATTR, '1');

        tabs.addEventListener('change', (e) => {
          const r = e.target && e.target.matches?.('.compare-tabs__input') ? e.target : null;
          if (!r || !r.checked) return;
          const group = r.dataset?.group || '';
          try {
            localStorage.setItem(storageKeyFor(tabs), group);
          } catch {}
        });
      }

      function init(root = document) {
        const list = root.querySelectorAll?.('.compare-tabs');
        if (!list || !list.length) return;
        list.forEach((tabs) => {
          restore(tabs);
          bind(tabs);
        });
      }

      function setupGlobal() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => init(document), {
            once: true
          });
        } else {
          init(document);
        }

        document.addEventListener('product-lists:change', () => init(document));
        document.addEventListener('shopify:section:load', (e) => init(e.target || document));
        document.addEventListener('shopify:section:select', (e) => init(e.target || document));
        document.addEventListener('shopify:section:reorder', (e) => init(e.target || document));

        const mo = new MutationObserver((mut) => {
          for (const m of mut) {
            for (const n of m.addedNodes || []) {
              if (!(n instanceof Element)) continue;
              if (n.matches?.('.compare-tabs') || n.querySelector?.('.compare-tabs')) {
                init(n);
              }
            }
          }
        });
        try {
          mo.observe(document.documentElement, {
            childList: true,
            subtree: true
          });
        } catch {}
      }

      return {
        init,
        setupGlobal
      };
    })();

    const def = (name, cls) => {
      if (!customElements.get(name)) customElements.define(name, cls);
    };
    def('product-lists-share-button', ProductListsShareButton);
    def('product-lists-print-button', ProductListsPrintButton);
    def('product-lists-import-shared-button', ProductListsImportSharedButton);
    def('product-lists-remove-button', ProductListsRemoveButton);
    def('product-lists-clear-all-button', ProductListsClearAllButton);
    def('compare-clear-group-button', CompareClearGroupButton);
    def('compare-hide-duplicate-values-toggle', CompareHideDuplicateValues);
    def('compare-hide-duplicate-values-toggle-clone', CompareHideDuplicateValuesClone);

    TabsManager.setupGlobal();
  });
});